import React from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import SchoolIcon from "@mui/icons-material/School";

const DEBUG = process.env.REACT_APP_DEBUG === "yes";
const debugLog = (...args) => {
  if (DEBUG) console.log(...args);
};

const StudentSelector = ({
  studentDropdown,
  setStudentDropdown,
  selectedScholarship,
  selectedStudents,
  students,
  affiliatedStudents,
  isBursary,
  isScholarship,
  cachedAcademicRankings,
  cachedNeedRankings,
  academicRankings,
  needRankings,
  toTitleCase,
}) => {
  return (
    <Box sx={{ flex: 4 }}>
      <FormControl fullWidth size="small" disabled={!selectedScholarship?.id}>
        <InputLabel id="student-select-label">Select Student</InputLabel>
        <Select
          labelId="student-select-label"
          label="Select Student"
          value={studentDropdown} // 🔥 Track dropdown state
          onChange={(e) => setStudentDropdown(e.target.value)}
        >
          {(() => {
            // 🧮 Top academic (Scholarship) rankings

            debugLog("🔍 Academic Rankings", academicRankings);
            debugLog("🔍 Need Rankings", needRankings);
            return students.map((student) => {
              const hasFinancialNeed = Boolean(student.bursary_reason?.trim());

              const isAffiliated = affiliatedStudents.some(
                (s) => s.id === student.id
              );
              const isAcademicRanked = academicRankings?.hasOwnProperty(
                student.id
              );
              const totalIncome = Number(student.bursary_total_income);
              const showIncome =
                isBursary &&
                hasFinancialNeed &&
                !isNaN(totalIncome) &&
                totalIncome > 0;

              const highlight = isBursary
                ? hasFinancialNeed
                : (isScholarship && isAcademicRanked) || isAffiliated;

              const rank = isBursary
                ? cachedNeedRankings[student.id]
                : isScholarship
                ? cachedAcademicRankings[student.id]
                : undefined;

              const isSelected = selectedStudents.some(
                (s) => s.id === student.id
              );

              const totalAwarded = student.awardHistory?.reduce(
                (sum, a) => sum + (a?.award_amount || 0),
                0
              );

              const numAwards = student.awardHistory?.length || 0;

              return (
                <MenuItem
                  key={student.id}
                  value={student.id}
                  disabled={isSelected} // optional: prevent re-selecting
                  sx={{
                    backgroundColor: isAffiliated
                      ? "#E3F2FD" // Light blue for affiliated students
                      : isBursary && hasFinancialNeed
                      ? "#FFF3E0"
                      : isScholarship && isAcademicRanked
                      ? "#E8F5E9"
                      : "inherit",
                    borderLeft: isAffiliated
                      ? "4px solid #2196f3" // Blue border for affiliation
                      : isBursary && hasFinancialNeed
                      ? "4px solid orange"
                      : isScholarship && isAcademicRanked
                      ? "4px solid green"
                      : "none",
                    fontWeight: highlight ? "bold" : "normal",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {highlight && rank && (
                      <strong style={{ marginRight: 6 }}>#{rank}</strong>
                    )}
                    {toTitleCase(student.last_name)},{" "}
                    {toTitleCase(student.first_name)} ({student.school})
                    {totalAwarded > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          marginLeft: 1,
                          color: "green",
                          fontStyle: "italic",
                        }}
                      >
                        {numAwards} award{numAwards > 1 ? "s" : ""} ($
                        {totalAwarded.toLocaleString()})
                      </Typography>
                    )}
                    {isSelected && (
                      <Typography
                        variant="caption"
                        sx={{
                          marginLeft: "8px",
                          color: "gray",
                          fontStyle: "italic",
                        }}
                      >
                        (Selected)
                      </Typography>
                    )}
                    {rank && (
                      <SchoolIcon
                        fontSize="small"
                        sx={{ ml: 1, color: "#1976d2" }}
                      />
                    )}
                    {isAffiliated && (
                      <Typography
                        variant="caption"
                        sx={{
                          marginLeft: 1,
                          color: "#2196f3",
                          fontStyle: "italic",
                        }}
                      >
                        Affiliated
                      </Typography>
                    )}
                    {isBursary && showIncome && (
                      <Typography
                        variant="caption"
                        sx={{
                          marginLeft: "8px",
                          color: "orange",
                          fontStyle: "italic",
                        }}
                      >
                        Income: ${totalIncome.toLocaleString()}
                      </Typography>
                    )}
                  </Box>

                  {isScholarship && (
                    <Typography variant="caption" sx={{ color: "gray" }}>
                      GPA: {student.GPA || "N/A"}, Grade:{" "}
                      {student.GradePercent || "N/A"}%
                    </Typography>
                  )}
                </MenuItem>
              );
            });
          })()}
        </Select>
      </FormControl>
    </Box>
  );
};

export default StudentSelector;
